/**
 * unreadBackfill — on-demand backward pagination to anchor the read marker in
 * a room's live timeline (NED-3439).
 *
 * After a refresh the Matrix client performs an initial sync with
 * initialSyncLimit=10 events per room. If the read marker (m.fully_read or
 * receipt) is older than that window, `resolveUnreadCount` cannot compute the
 * manual count and the badge creeps up only as decryption events arrive —
 * producing the visible "drop then creep" symptom.
 *
 * This helper paginates the live timeline backwards in bounded chunks until
 * the marker appears (so manual count becomes trusted), or a cap is reached.
 * It is resilient to concurrent callers and cancellation.
 *
 * Module state is intentionally per-session: it is cleared on logout via
 * `clearAllUnreadBackfillState` and lost entirely on `window.location.reload()`
 * (no cross-refresh memoisation). See the NED-3439 plan for rationale.
 */

import { NotificationCountType } from '../constants/matrixEvents';
import logger from './logger';

const CAP = 500;
const CHUNK = 50;
const BACKOFF_MS = 60_000;

const inFlight = new Map();           // roomId → { promise, controller }
const backoffUntil = new Map();       // roomId → epoch ms
const lastCappedOutcome = new Map();  // roomId → { found, paginated, cappedOut, aborted }
const capTimeSdkCount = new Map();    // roomId → number (SDK count observed at cap)
// Remember the last outcome KIND we emitted per room so callers can skip
// duplicate re-renders (NED-3439 plan Workstream B). Values: 'found' |
// 'cappedOut' | 'exhausted' | 'aborted' | 'idle'.
const lastEmittedOutcome = new Map();

/**
 * @returns {number | undefined} the SDK unread count captured when the most
 * recent backfill for `roomId` hit the pagination cap, or undefined if that
 * room has not been capped out.
 */
export function getCapTimeSdkCount(roomId) {
  return capTimeSdkCount.get(roomId);
}

/**
 * Abort all in-flight backfills. Used in hook unmount / logout teardown.
 * Does NOT clear backoff or capTime state (those remain informative).
 */
export function abortAllBackfills() {
  for (const { controller } of inFlight.values()) {
    try { controller.abort(); } catch { /* swallow */ }
  }
}

/**
 * Clear every module-level map. Called on logout and on test teardown.
 */
export function clearAllUnreadBackfillState() {
  abortAllBackfills();
  inFlight.clear();
  backoffUntil.clear();
  lastCappedOutcome.clear();
  capTimeSdkCount.clear();
  lastEmittedOutcome.clear();
}

/**
 * @param {{found: boolean, cappedOut: boolean, aborted: boolean}} outcome
 * @returns {'found' | 'cappedOut' | 'aborted' | 'exhausted'}
 */
function classifyOutcome(outcome) {
  if (outcome.found) return 'found';
  if (outcome.cappedOut) return 'cappedOut';
  if (outcome.aborted) return 'aborted';
  return 'exhausted';
}

/**
 * Tag an outcome with `alreadyEmitted` based on the last kind emitted for
 * this room. Callers skip their re-render when `alreadyEmitted === true`
 * to break the 150ms churn during the BACKOFF_MS cap-out window.
 */
function withEmittedFlag(roomId, outcome) {
  const kind = classifyOutcome(outcome);
  const prev = lastEmittedOutcome.get(roomId);
  const alreadyEmitted = prev === kind;
  lastEmittedOutcome.set(roomId, kind);
  return { ...outcome, alreadyEmitted };
}

/**
 * Run async tasks with bounded concurrency. Tasks are invoked lazily (only
 * when a slot frees up). Keeps implementation dependency-free.
 *
 * @param {Array<() => Promise<any>>} tasks
 * @param {number} limit
 */
export async function runWithLimit(tasks, limit) {
  const executing = new Set();
  for (const task of tasks) {
    const p = Promise.resolve().then(task).finally(() => executing.delete(p));
    executing.add(p);
    if (executing.size >= limit) {
      await Promise.race(executing);
    }
  }
  return Promise.all(executing);
}

/**
 * Ensure the given read marker appears in the room's live timeline.
 *
 * @param {import('matrix-js-sdk').MatrixClient} client
 * @param {import('matrix-js-sdk').Room} room
 * @param {string | null} markerEventId — result of getEffectiveReadMarkerEventId
 * @returns {Promise<{
 *   found: boolean,
 *   paginated: number,
 *   cappedOut: boolean,
 *   aborted: boolean,
 * }>}
 */
export function ensureReadMarkerInTimeline(client, room, markerEventId) {
  // No marker yet (account data hasn't arrived) — retry when m.fully_read fires.
  if (!markerEventId || !room || !client) {
    const roomIdForFlag = room?.roomId;
    const outcome = { found: false, paginated: 0, cappedOut: false, aborted: false };
    return Promise.resolve(
      roomIdForFlag ? withEmittedFlag(roomIdForFlag, outcome) : { ...outcome, alreadyEmitted: false },
    );
  }

  const roomId = room.roomId;

  // Short-circuit: marker is already in the live timeline.
  const liveTimeline = room.getLiveTimeline?.();
  const events = liveTimeline?.getEvents?.() || [];
  if (events.some(e => e.getId() === markerEventId)) {
    return Promise.resolve(withEmittedFlag(roomId, { found: true, paginated: 0, cappedOut: false, aborted: false }));
  }

  // Active call for this room — share the promise.
  const existing = inFlight.get(roomId);
  if (existing) return existing.promise;

  // In back-off from a recent cap-out — return cached outcome without work.
  const untilTs = backoffUntil.get(roomId);
  if (untilTs && untilTs > Date.now()) {
    const cached = lastCappedOutcome.get(roomId) || { found: false, paginated: 0, cappedOut: true, aborted: false };
    return Promise.resolve(withEmittedFlag(roomId, cached));
  }

  const controller = new AbortController();
  const promise = (async () => {
    let paginated = 0;
    let found = false;
    let cappedOut = false;
    let aborted = false;

    try {
      // re-check after async entry in case the marker appeared
      const initialEvents = liveTimeline?.getEvents?.() || [];
      if (initialEvents.some(e => e.getId() === markerEventId)) {
        return withEmittedFlag(roomId, { found: true, paginated: 0, cappedOut: false, aborted: false });
      }

      while (!aborted && !found && paginated < CAP) {
        if (controller.signal.aborted) { aborted = true; break; }

        let more;
        try {
          more = await client.paginateEventTimeline(liveTimeline, { backwards: true, limit: CHUNK });
        } catch (err) {
          logger.warn('[unreadBackfill] paginateEventTimeline failed', {
            roomId,
            error: err?.message || String(err),
          });
          break;
        }

        if (controller.signal.aborted) { aborted = true; break; }

        paginated += CHUNK;

        const now = liveTimeline?.getEvents?.() || [];
        if (now.some(e => e.getId() === markerEventId)) {
          found = true;
          break;
        }

        if (more === false) {
          // reached room creation / no more history
          break;
        }
      }

      if (!found && paginated >= CAP) {
        cappedOut = true;
        backoffUntil.set(roomId, Date.now() + BACKOFF_MS);
        try {
          capTimeSdkCount.set(
            roomId,
            room.getUnreadNotificationCount?.(NotificationCountType.Total) || 0,
          );
        } catch { /* ignore */ }
      }

      const outcome = { found, paginated, cappedOut, aborted };
      if (cappedOut) lastCappedOutcome.set(roomId, outcome);
      return withEmittedFlag(roomId, outcome);
    } finally {
      inFlight.delete(roomId);
    }
  })();

  inFlight.set(roomId, { promise, controller });
  return promise;
}
